import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import "../styles/LegalPages.css";

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <SEO
        title="Privacy Policy - Grad4Grad"
        description="Learn how Grad4Grad collects, uses, and protects your personal information in our comprehensive Privacy Policy."
        keywords="privacy policy, data protection, personal information, grad4grad privacy"
        url="https://grad4grad.com/privacy"
        type="website"
      />

      <div className="legal-container">
        <div className="legal-header">
          <h1>Privacy Policy</h1>
          <p>Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              At Grad4Grad ("we," "our," or "us"), we are committed to
              protecting your privacy and ensuring the security of your personal
              information. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you use our study
              abroad guidance platform.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>

            <h3>2.1 Personal Information</h3>
            <p>We may collect the following personal information:</p>
            <ul>
              <li>Name and email address (when you create an account)</li>
              <li>Profile information (education background, interests)</li>
              <li>Communication preferences</li>
              <li>Google account information (when using Google login)</li>
            </ul>

            <h3>2.2 Usage Information</h3>
            <p>
              We automatically collect certain information when you use our
              service:
            </p>
            <ul>
              <li>IP address and device information</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent</li>
              <li>Search queries and preferences</li>
              <li>Error logs and performance data</li>
            </ul>

            <h3>2.3 Cookies and Tracking</h3>
            <p>
              We use cookies and similar technologies to enhance your
              experience, analyze usage patterns, and provide personalized
              content. You can control cookie settings through your browser
              preferences.
            </p>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li>Provide and maintain our services</li>
              <li>Personalize your experience and recommendations</li>
              <li>Process your requests and inquiries</li>
              <li>Send important updates and notifications</li>
              <li>Improve our services and develop new features</li>
              <li>Ensure security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2>4. Information Sharing and Disclosure</h2>
            <p>
              We do not sell, trade, or rent your personal information to third
              parties. We may share your information in the following
              circumstances:
            </p>

            <h3>4.1 Service Providers</h3>
            <p>
              We may share information with trusted third-party service
              providers who assist us in operating our website, conducting
              business, or servicing you.
            </p>

            <h3>4.2 Legal Requirements</h3>
            <p>
              We may disclose your information if required by law or in response
              to valid legal requests, such as subpoenas or court orders.
            </p>

            <h3>4.3 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or sale of assets, your
              information may be transferred as part of the business
              transaction.
            </p>

            <h3>4.4 Consent</h3>
            <p>
              We may share your information with your explicit consent or as
              otherwise described in this policy.
            </p>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to
              protect your personal information against unauthorized access,
              alteration, disclosure, or destruction. These measures include:
            </p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication</li>
              <li>Secure hosting and infrastructure</li>
              <li>Employee training on data protection</li>
            </ul>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to
              fulfill the purposes outlined in this Privacy Policy, unless a
              longer retention period is required or permitted by law. When we
              no longer need your information, we will securely delete or
              anonymize it.
            </p>
          </section>

          <section>
            <h2>7. Your Rights and Choices</h2>
            <p>
              You have the following rights regarding your personal information:
            </p>

            <h3>7.1 Access and Update</h3>
            <p>
              You can access and update your personal information through your
              account settings or by contacting us directly.
            </p>

            <h3>7.2 Deletion</h3>
            <p>
              You can request deletion of your account and associated personal
              information, subject to certain legal and contractual obligations.
            </p>

            <h3>7.3 Opt-out</h3>
            <p>
              You can opt out of marketing communications and certain data
              processing activities by updating your preferences or contacting
              us.
            </p>

            <h3>7.4 Data Portability</h3>
            <p>
              You can request a copy of your personal information in a
              structured, machine-readable format.
            </p>
          </section>

          <section>
            <h2>8. Third-Party Services</h2>
            <p>
              Our service may contain links to third-party websites or integrate
              with third-party services. We are not responsible for the privacy
              practices of these third parties. We encourage you to review their
              privacy policies before providing any personal information.
            </p>
          </section>

          <section>
            <h2>9. Children's Privacy</h2>
            <p>
              Our service is not intended for children under the age of 13. We
              do not knowingly collect personal information from children under
              13. If you are a parent or guardian and believe your child has
              provided us with personal information, please contact us
              immediately.
            </p>
          </section>

          <section>
            <h2>10. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries
              other than your own. We ensure that such transfers comply with
              applicable data protection laws and implement appropriate
              safeguards to protect your information.
            </p>
          </section>

          <section>
            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will
              notify you of any material changes by posting the new Privacy
              Policy on this page and updating the "Last updated" date. We
              encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section>
            <h2>12. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our privacy
              practices, please contact us at:
            </p>
            <ul>
              <li>Email: contact@grad4grad.com</li>
              <li>
                Website: <a href="https://grad4grad.com">grad4grad.com</a>
              </li>
            </ul>
          </section>

          <section>
            <h2>13. Governing Law</h2>
            <p>
              This Privacy Policy is governed by and construed in accordance
              with the laws of India. Any disputes arising from this policy will
              be subject to the exclusive jurisdiction of the courts in India.
            </p>
          </section>
        </div>

        <div className="legal-footer">
          <Link to="/" className="back-link">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
